import { toRaw } from 'vue'
import { logService } from '@nsftx/seven-client-retail-utils'
import type { BetslipView } from '@nsftx/seven-client-sdk-integrator'
import { useFetchBetslip } from '#src/composables/useFetchBetslip'
import TicketResolveError from '#src/classes/TicketResolveError'
import { useTicketsStore } from '#src/stores/tickets/ticketsStore'

const TICKET_CHECKER_RETRY_TIMEOUT_IN_MS = 5_000
const TICKET_CHECK_HTTP_RETRY_COUNT = 3
const LOG_PREFIX = '[betslipCheckerService]'
const waitBeforeRetry = () =>
  new Promise<void>((resolve) => window.setTimeout(resolve, TICKET_CHECKER_RETRY_TIMEOUT_IN_MS))

const resolveBetslipByFeBetslipId = async (feBetslipId: string): Promise<BetslipView> => {
  const { betslip, fetchBetslip, errorDetails } = useFetchBetslip()

  await fetchBetslip(feBetslipId)

  if (!betslip.value) {
    return Promise.reject(
      new TicketResolveError(
        'Betslip API request failed.',
        'SGP_TICKET_RESOLVE_BETSLIP_HTTP_FAILED',
        {
          context: {
            feBetslipId,
            errorDetails: errorDetails.value
          }
        }
      )
    )
  }

  const betslipStatusPhase = betslip.value.status.phase
  const isBetslipAccepted = betslipStatusPhase === 'PLACED'
  const isBetslipFailed = betslipStatusPhase === 'CANCELLED' || betslipStatusPhase === 'REJECTED'

  if (isBetslipAccepted || isBetslipFailed) {
    return betslip.value
  }

  const isBetslipStarted = betslipStatusPhase === 'STARTED'
  if (isBetslipStarted) {
    return Promise.reject(
      new TicketResolveError(
        `Betslip phase is ${betslipStatusPhase}.`,
        'SGP_TICKET_RESOLVE_BETSLIP_HTTP_FAILED',
        {
          context: {
            feBetslipId
          }
        }
      )
    )
  }

  return Promise.reject(
    new TicketResolveError(`Betslip phase is unknown.`, 'SGP_TICKET_RESOLVE_BETSLIP_HTTP_FAILED', {
      context: {
        feBetslipId
      }
    })
  )
}

const resolveBetslipByFeBetslipIdWithRetry = async (feBetslipId: string) => {
  let error: unknown | undefined
  let attempt = 0

  while (attempt < TICKET_CHECK_HTTP_RETRY_COUNT) {
    try {
      const { isTicketResolved, endChecker } = useTicketsStore()
      const resolvedTicket = isTicketResolved(feBetslipId)

      if (resolvedTicket && resolvedTicket.betslip) {
        endChecker(feBetslipId)
        return resolvedTicket.betslip
      }

      const betslip = await resolveBetslipByFeBetslipId(feBetslipId)
      return betslip
    } catch (err) {
      error = err
      logService.error(`${LOG_PREFIX} Failed to resolve betslip with #${attempt + 1} attempt.`, {
        code: 'SGP_BETSLIP_CHECKER_RESOLVE_ERROR',
        feBetslipId,
        details:
          error instanceof TicketResolveError ? toRaw(error.options.context.errorDetails) : null
      })

      if (attempt < TICKET_CHECK_HTTP_RETRY_COUNT - 1) {
        await waitBeforeRetry()
      }
    }

    attempt++
  }

  throw error
}

const createBetslipChecker = (feBetslipId: string) => {
  let betslipCheckTimeout: number | undefined

  const startChecker = () => {
    endChecker()
    return resolveBetslipByFeBetslipIdWithRetry(feBetslipId)
  }

  const endChecker = () => {
    window.clearTimeout(betslipCheckTimeout)
  }

  return {
    startChecker,
    endChecker
  }
}

export { createBetslipChecker }
